import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { callStatus } from '../redux/constants';

import { useMessageCommunicationContext } from '../contexts/MessageCommunicationContext';
import { debounce } from 'lodash';
import { authApi } from '../utils/api';
import { sessionExpired } from '../redux/SessionActions';

const useUpdateLastActivity = () => {
  const { postMessage } = useMessageCommunicationContext();
  const dispatch = useDispatch();

  const auth = useSelector(({ auth }) => auth);
  const context = useSelector(({ context }) => context);
  const slMainForceLogoutTimeoutMins = useSelector(
    ({
      main: {
        data: { logout_timeout_mins },
      },
    }) => logout_timeout_mins
  );
  const translations = useSelector(({ translations }) => translations);

  const [slMainForceLogout, setSlMainForceLogout] = useState(null);

  const intervalCheck = useRef();

  useEffect(() => {
    let forceLogout = null;
    if (
      context?.configVars?.SL_MAIN_FORCE_LOGOUT != null &&
      slMainForceLogoutTimeoutMins != null
    ) {
      forceLogout =
        context?.configVars?.SL_MAIN_FORCE_LOGOUT > slMainForceLogoutTimeoutMins
          ? context?.configVars?.SL_MAIN_FORCE_LOGOUT
          : slMainForceLogoutTimeoutMins;
    } else if (context?.configVars?.SL_MAIN_FORCE_LOGOUT != null) {
      forceLogout = context?.configVars?.SL_MAIN_FORCE_LOGOUT;
    } else if (slMainForceLogoutTimeoutMins != null) {
      forceLogout = slMainForceLogoutTimeoutMins;
    }

    if (forceLogout) {
      setSlMainForceLogout(forceLogout);
    }
  }, [context, slMainForceLogoutTimeoutMins]);

  useEffect(() => {
    const handleMessage = (event) => {
      // IMPORTANT: Always validate the origin to prevent CSRF
      // if (event.origin !== process.env.REACT_APP_API_URL_SMARTLIS) return;

      if (event.data.cmd === 'update_last_activity') {
        localStorage.setItem('lastActivity', Date.now());
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (
      auth.status === callStatus.SUCCESS &&
      slMainForceLogout &&
      slMainForceLogout > 0
    ) {
      const startIntervalCheck = () => {
        intervalCheck.current = setInterval(() => {
          const lastActivity = localStorage.getItem('lastActivity');
          if (lastActivity) {
            const timeDiffMins = (Date.now() - parseInt(lastActivity)) / 60000;
            if (timeDiffMins > slMainForceLogout) {
              localStorage.removeItem('lastActivity');
              const messageContent = translations.data.login.user_logged_out;
              authApi.get('/logout/').finally(() => {
                dispatch(sessionExpired(messageContent));
              });
            }
          }
        }, 1000); // Check every second
      };

      if (!intervalCheck.current) {
        startIntervalCheck();
      }
    } else {
      if (intervalCheck.current) {
        clearInterval(intervalCheck.current);
        intervalCheck.current = null;
      }
    }

    return () => {
      if (intervalCheck.current) {
        clearInterval(intervalCheck.current);
        intervalCheck.current = null;
      }
    };
  }, [auth.status, slMainForceLogout, dispatch, postMessage, translations]);

  const handleActivity = debounce(() => {
    localStorage.setItem('lastActivity', Date.now());
  }, 250);

  useEffect(() => {
    return () => {
      handleActivity.cancel();
    };
  }, [handleActivity]);

  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'];

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, {
        capture: true,
        passive: true,
      });
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity, { capture: true });
      });
    };
  }, [handleActivity]);
};

export default useUpdateLastActivity;
